import React, { FC, useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import './ClientSelectionStep.scss';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { useAddTrainerClient } from 'src/api/mutations/useAddTrainerClient';
import { Button } from 'src/screens/OnboardingTrainer/components/Button/Button';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as RadioTickIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/radio-tick-2.svg';
import { ReactComponent as NextIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/next.svg';
import { isValidEmail } from 'src/utils/validation';
import { sessionStore } from 'src/store/sessionStore';

interface ClientSelectionStepProps {
  onNext?: (clientId?: string) => void;
  onClientSelect?: (name: string, uid?: string, bodyProfile?: string) => void;
  value?: string | null;
  isLoading?: boolean;
}

// Who is this program for?
export const ClientSelectionStep: FC<ClientSelectionStepProps> = ({
  onNext,
  onClientSelect,
  value,
  isLoading: isNextLoading = false,
}) => {
  const [tab, setTab] = useState<'list' | 'new'>('list');
  const [userManuallySelectedTab, setUserManuallySelectedTab] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>(value || '');

  const [newName, setNewName] = useState('');
  const [newSurname, setNewSurname] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newBodyProfile, setNewBodyProfile] = useState<'female' | 'male' | ''>('');

  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    surname?: string;
    email?: string;
    bodyProfile?: string;
  }>({});
  const [touchedFields, setTouchedFields] = useState<{
    name?: boolean;
    surname?: boolean;
    email?: boolean;
    bodyProfile?: boolean;
  }>({});
  const [serverError, setServerError] = useState<string>('');

  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading } = useGetTrainerClients('active', '', 'name', currentPage, 50);
  const totalCount = data?.totalCount || 0;

  const [allClients, setAllClients] = useState<any[]>([]);

  useEffect(() => {
    const fetchedClients = data?.clients;
    if (fetchedClients && fetchedClients.length > 0) {
      if (currentPage === 1) {
        setAllClients(fetchedClients);
      } else {
        setAllClients((prev) => {
          const existingIds = new Set(prev.map((c) => c.uid));
          const newClients = fetchedClients.filter((c: any) => !existingIds.has(c.uid));
          return [...prev, ...newClients];
        });
      }
    } else if (fetchedClients && fetchedClients.length === 0 && currentPage === 1) {
      setAllClients([]);
    }
  }, [data?.clients, currentPage]);
  const { mutate: addClient, isPending: isAdding } = useAddTrainerClient();

  const listRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (listRef.current) {
        setHasScroll(listRef.current.scrollHeight > listRef.current.clientHeight);
      }
    };
    checkScroll();
    // Re-check after a short delay in case of layout shifts
    const timer = setTimeout(checkScroll, 100);
    window.addEventListener('resize', checkScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScroll);
    };
  }, [allClients, tab]);

  // Auto-switch based on data availability (only if user hasn't manually selected)
  useEffect(() => {
    if (!isLoading && data && !userManuallySelectedTab) {
      if (data.totalCount === 0) {
        setTab('new');
      } else {
        setTab('list');
      }
    }
  }, [isLoading, data, userManuallySelectedTab]);

  // Auto-scroll to selected client when tab is 'list'
  useEffect(() => {
    if (tab === 'list' && selectedClient && listRef.current) {
      // Use setTimeout to ensure DOM has updated with the new client before querying
      const timer = setTimeout(() => {
        const selectedEl = listRef.current?.querySelector('.client-item.selected');
        if (selectedEl) {
          selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [tab, selectedClient, allClients]);

  const getInitials = (name: string, surname: string) => {
    const first = name ? name.charAt(0).toUpperCase() : '';
    const second = surname ? surname.charAt(0).toUpperCase() : '';
    return `${first}${second}`;
  };

  const validateName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return 'First Name is required';
    if (name.length > 50) return 'First Name must be 50 characters or less';
    if (!/\p{L}/u.test(trimmed)) return 'First Name must contain at least one valid letter';
    return '';
  };

  const validateSurname = (surname: string) => {
    const trimmed = surname.trim();
    if (!trimmed) return 'Surname is required';
    if (surname.length > 50) return 'Surname must be 50 characters or less';
    if (!/\p{L}/u.test(trimmed)) return 'Surname must contain at least one valid letter';
    return '';
  };

  const currentUser = sessionStore((s) => s.currentUser);

  const validateEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && trimmed.length > 255) return 'Email must be 255 characters or less';
    if (trimmed && !isValidEmail(trimmed)) return 'Please enter a valid email address.';
    if (currentUser?.email && trimmed.toLowerCase() === currentUser.email.trim().toLowerCase()) {
      return 'You cannot add yourself as a client.';
    }
    return '';
  };

  const validateBodyProfile = (profile: string) => {
    if (!profile) return 'Please select a body profile.';
    return '';
  };

  const resetForm = () => {
    setNewName('');
    setNewSurname('');
    setNewEmail('');
    setNewBodyProfile('');
    setFieldErrors({});
    setTouchedFields({});
    setServerError('');
  };

  const handleAddClient = () => {
    const nameErr = validateName(newName);
    const surnameErr = validateSurname(newSurname);
    const emailErr = validateEmail(newEmail);
    const bodyProfileErr = validateBodyProfile(newBodyProfile);

    setTouchedFields({
      name: true,
      surname: true,
      email: true,
      bodyProfile: true,
    });
    setFieldErrors({
      name: nameErr,
      surname: surnameErr,
      email: emailErr,
      bodyProfile: bodyProfileErr,
    });

    if (nameErr || surnameErr || emailErr || bodyProfileErr) {
      return;
    }

    setServerError('');
    addClient(
      {
        name: newName.trim(),
        surname: newSurname.trim(),
        email: newEmail.trim(),
        bodyProfile: newBodyProfile,
      },
      {
        onSuccess: (res) => {
          const newUid = res?.data?.uid || res?.uid;
          const fullName = `${newName.trim()} ${newSurname.trim()}`;
          toast.success(`${fullName} added successfully`, {
            id: 'add-client-success',
          });

          // Reset new client form fields
          resetForm();

          if (newUid) {
            setSelectedClient(newUid);
            if (onClientSelect) {
              onClientSelect(fullName, newUid, newBodyProfile);
            }
            if (onNext) {
              onNext(newUid);
            } else {
              setCurrentPage(1);
              setTab('list');
            }
          } else {
            setCurrentPage(1);
            setTab('list');
          }
        },
        onError: (err: any) => {
          const ERROR_MAPPING: Record<string, string> = {
            ERR_CLIENT_EXISTS: 'This client is already in your list.',
            ERR_CANNOT_INVITE_SELF: 'You cannot add yourself as a client.',
            ERR_FIRST_NAME_REQUIRED: 'First Name is required',
            ERR_SURNAME_REQUIRED: 'Surname is required',
            ERR_FIRST_NAME_TOO_LONG: 'First Name must be 50 characters or less',
            ERR_SURNAME_TOO_LONG: 'Surname must be 50 characters or less',
            ERR_EMAIL_TOO_LONG: 'Email must be 255 characters or less',
            ERR_INVALID_FIRST_NAME: 'First Name must contain at least one valid letter',
            ERR_INVALID_SURNAME: 'Surname must contain at least one valid letter',
            ERR_INVALID_EMAIL_FORMAT: 'Please enter a valid email address.',
            ERR_AUTH_CREATION_FAILED: 'Failed to create user account',
            ERR_BODY_PROFILE_REQUIRED: 'Please select a body profile.',
            ERR_INVALID_BODY_PROFILE: 'Please select a valid body profile.',
          };

          const code = err.errorCode || '';
          const msg =
            ERROR_MAPPING[code] ||
            (err.message === 'CLIENT_EXISTS' ||
            err?.message?.toLowerCase().includes('already exists')
              ? 'This client is already in your list.'
              : err.message === 'INVALID_EMAIL' || err?.message?.includes('INVALID_EMAIL')
                ? 'Please enter a valid email address.'
                : err.message || 'Failed to add client. Please try again.');

          if (
            code === 'ERR_FIRST_NAME_REQUIRED' ||
            code === 'ERR_FIRST_NAME_TOO_LONG' ||
            code === 'ERR_INVALID_FIRST_NAME'
          ) {
            setFieldErrors((prev) => ({ ...prev, name: msg }));
            setTouchedFields((prev) => ({ ...prev, name: true }));
          } else if (
            code === 'ERR_SURNAME_REQUIRED' ||
            code === 'ERR_SURNAME_TOO_LONG' ||
            code === 'ERR_INVALID_SURNAME'
          ) {
            setFieldErrors((prev) => ({ ...prev, surname: msg }));
            setTouchedFields((prev) => ({ ...prev, surname: true }));
          } else if (code === 'ERR_EMAIL_TOO_LONG' || code === 'ERR_INVALID_EMAIL_FORMAT') {
            setFieldErrors((prev) => ({ ...prev, email: msg }));
            setTouchedFields((prev) => ({ ...prev, email: true }));
          } else if (code === 'ERR_INVALID_BODY_PROFILE' || code === 'ERR_BODY_PROFILE_REQUIRED') {
            setFieldErrors((prev) => ({ ...prev, bodyProfile: msg }));
            setTouchedFields((prev) => ({ ...prev, bodyProfile: true }));
          } else {
            setServerError(msg);
          }
        },
      },
    );
  };

  const isFormInvalid =
    !newName.trim() ||
    !newSurname.trim() ||
    newName.length > 50 ||
    newSurname.length > 50 ||
    !/\p{L}/u.test(newName.trim()) ||
    !/\p{L}/u.test(newSurname.trim()) ||
    (newEmail.trim() !== '' && (newEmail.length > 255 || !isValidEmail(newEmail.trim()))) ||
    newBodyProfile === '';

  return (
    <div className="client-selection-step">
      <h2 className="step-title">{'Who is this program for?'}</h2>
      <p className="step-subtitle">{'Let’s select a client from your list.'}</p>

      <div className="tab-toggle">
        <button
          className={`tab ${tab === 'list' ? 'active' : ''}`}
          onClick={() => {
            setTab('list');
            setUserManuallySelectedTab(true);
            resetForm();
          }}
        >
          {'Choose from your list'}
        </button>
        <button
          className={`tab ${tab === 'new' ? 'active' : ''}`}
          onClick={() => {
            setTab('new');
            setUserManuallySelectedTab(true);
            resetForm();
          }}
        >
          {'Add New'}
        </button>
      </div>

      {tab === 'list' && (
        <div className="client-list-wrapper">
          <div
            className={`client-list ${hasScroll ? 'has-scroll' : ''}`}
            ref={listRef}
            id="clientSelectionList"
          >
            {isLoading && allClients.length === 0 && (
              <div className="loading-container" style={{ minHeight: 'auto', padding: '20px' }}>
                <p>{'Loading clients...'}</p>
                <svg
                  className="loading-spinner"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              </div>
            )}
            {!isLoading && allClients.length === 0 && (
              <p className="empty-message">{'No clients found. Add a new one!'}</p>
            )}
            <InfiniteScroll
              dataLength={allClients.length}
              next={() => setCurrentPage((prev) => prev + 1)}
              hasMore={allClients.length < totalCount}
              loader={
                <div
                  className="loading-container"
                  style={{ padding: '20px 0', minHeight: 'auto', flex: 'none' }}
                >
                  <p>{'Loading more clients...'}</p>
                  <svg
                    className="loading-spinner"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                </div>
              }
              scrollableTarget="clientSelectionList"
              style={{ overflow: 'hidden' }}
            >
              {[...allClients]
                .sort((a, b) => {
                  const aName = `${a.name} ${a.surname}`.toLowerCase();
                  const bName = `${b.name} ${b.surname}`.toLowerCase();
                  return aName.localeCompare(bName);
                })
                .map((client) => (
                  <div
                    className={`client-item ${selectedClient === client.uid ? 'selected' : ''}`}
                    key={client.uid}
                    onClick={() => {
                      setSelectedClient(client.uid);
                      if (onClientSelect)
                        onClientSelect(
                          `${client.name} ${client.surname}`,
                          client.uid,
                          client.bodyProfile,
                        );
                    }}
                  >
                    <div className="client-info">
                      <div className="client-avatar">
                        {client.avatarUrl ? (
                          <img src={client.avatarUrl} alt={client.name} className="avatar-img" />
                        ) : (
                          <div className="avatar-placeholder">
                            <span className="initials">
                              {getInitials(client.name, client.surname)}
                            </span>
                          </div>
                        )}
                      </div>
                      <span className="client-name">
                        {client.name} {client.surname}
                      </span>
                    </div>
                    <div className="client-radio">
                      {selectedClient === client.uid ? (
                        <RadioTickIcon />
                      ) : (
                        <div className="radio-circle"></div>
                      )}
                    </div>
                  </div>
                ))}
            </InfiniteScroll>
          </div>
          {allClients.length > 3 && <div className="list-fade-overlay"></div>}
        </div>
      )}

      {tab === 'new' && (
        <div className="add-new-form">
          <div className="form-group body-profile-group">
            <label>{'BODY PROFILE'}</label>
            <div className="gender-selection">
              <label
                className="gender-option"
                onClick={() => {
                  setNewBodyProfile('female');
                  setTouchedFields((prev) => ({ ...prev, bodyProfile: true }));
                  setFieldErrors((prev) => ({ ...prev, bodyProfile: '' }));
                }}
              >
                <div className={`checkbox-custom ${newBodyProfile === 'female' ? 'checked' : ''}`}>
                  {newBodyProfile === 'female' && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span>Female</span>
              </label>
              <label
                className="gender-option"
                onClick={() => {
                  setNewBodyProfile('male');
                  setTouchedFields((prev) => ({ ...prev, bodyProfile: true }));
                  setFieldErrors((prev) => ({ ...prev, bodyProfile: '' }));
                }}
              >
                <div className={`checkbox-custom ${newBodyProfile === 'male' ? 'checked' : ''}`}>
                  {newBodyProfile === 'male' && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span>Male</span>
              </label>
            </div>
            {touchedFields.bodyProfile && fieldErrors.bodyProfile && (
              <p className="error-message">{fieldErrors.bodyProfile}</p>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{'NAME'}</label>
              <input
                type="text"
                placeholder="Name"
                maxLength={50}
                value={newName}
                onChange={(e) => {
                  const val = e.target.value;
                  setNewName(val);
                  setServerError('');
                  if (touchedFields.name) {
                    setFieldErrors((prev) => ({ ...prev, name: validateName(val) }));
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  setNewName(val);
                  setTouchedFields((prev) => ({ ...prev, name: true }));
                  setFieldErrors((prev) => ({ ...prev, name: validateName(val) }));
                }}
                className={touchedFields.name && fieldErrors.name ? 'error-border' : ''}
              />
              {touchedFields.name && fieldErrors.name && (
                <p className="error-message">{fieldErrors.name}</p>
              )}
            </div>

            <div className="form-group">
              <label>{'SURNAME'}</label>
              <input
                type="text"
                placeholder="Surname"
                maxLength={50}
                value={newSurname}
                onChange={(e) => {
                  const val = e.target.value;
                  setNewSurname(val);
                  setServerError('');
                  if (touchedFields.surname) {
                    setFieldErrors((prev) => ({ ...prev, surname: validateSurname(val) }));
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  setNewSurname(val);
                  setTouchedFields((prev) => ({ ...prev, surname: true }));
                  setFieldErrors((prev) => ({ ...prev, surname: validateSurname(val) }));
                }}
                className={touchedFields.surname && fieldErrors.surname ? 'error-border' : ''}
              />
              {touchedFields.surname && fieldErrors.surname && (
                <p className="error-message">{fieldErrors.surname}</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{'EMAIL(OPTIONAL)'}</label>
            <input
              type="email"
              placeholder="john.doe@example.com"
              maxLength={255}
              value={newEmail}
              onChange={(e) => {
                const val = e.target.value;
                setNewEmail(val);
                setServerError('');
                if (touchedFields.email) {
                  setFieldErrors((prev) => ({ ...prev, email: validateEmail(val) }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                setNewEmail(val);
                setTouchedFields((prev) => ({ ...prev, email: true }));
                setFieldErrors((prev) => ({ ...prev, email: validateEmail(val) }));
              }}
              className={touchedFields.email && fieldErrors.email ? 'error-border' : ''}
            />
            {touchedFields.email && fieldErrors.email && (
              <p className="error-message">{fieldErrors.email}</p>
            )}
          </div>

          {serverError && <p className="error-message server-error">{serverError}</p>}

          <Button
            variant="primary"
            className="next-btn right-arrow m-height-48 btn-primary-white add-client-btn"
            onClick={handleAddClient}
            disabled={isAdding || isNextLoading || isFormInvalid}
            svg={<NextIcon />}
            isLoading={isAdding || isNextLoading}
          >
            <span className="add-client-text">{'Add Client'}</span>
          </Button>
        </div>
      )}
    </div>
  );
};
